import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { IonicModule, ModalController, NavController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { EditEscalationTypeComponent } from 'src/app/components/edit-escalation-type/edit-escalation-type.component';
import { PRIVATE_CHANNEL_NAME } from 'src/app/constants';
import { EscalationType } from 'src/models';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { EscalationTypesService } from 'src/services/escalation-types.service';
import { MessageService } from 'src/services/message.service';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgxDatatableModule, SharedPipesModule],
  selector: 'app-escalation-types-list',
  templateUrl: './escalation-types-list.component.html',
  styleUrls: ['./escalation-types-list.component.scss'],
})
export class EscalationTypesListComponent implements OnInit, OnDestroy {
  escalationTypes: Partial<EscalationType>[] = [];
  filteredEscalationTypes: Partial<EscalationType>[] = [];

  searchText: string;
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  pageCount = 1;
  pageSize = 50;
  routerSub: Subscription;
  projectId: string;

  PRIVATE_CHANNEL_NAME = PRIVATE_CHANNEL_NAME;

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private navCtrl: NavController,
    private escalationTypesService: EscalationTypesService,
    private msgSrvc: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
  ) {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  async ngOnInit() {
    const currentPath = this.location.path();

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd && currentPath === (e as NavigationEnd).url))
      .subscribe(async () => {
        this.isLoading = true;

        await Promise.all([this.getEscalationTypes()]);

        this.isLoading = false;
      });

    this.route.queryParams.subscribe(async (params) => {
      if (params.id) {
        const modal = await this.modalCtrl.create({
          component: EditEscalationTypeComponent,
          cssClass: 'modal-tall',
          componentProps: {
            id: params.id,
            projectId: this.projectId,
          },
        });

        modal.present();

        modal.onDidDismiss().then(() => {
          this.navCtrl.navigateForward(this.router.url, {
            queryParams: {},
          });
          this.getEscalationTypes();
        });
      }
    });
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
  }

  async getEscalationTypes() {
    try {
      const response = await this.escalationTypesService.list({
        limit: null,
        order: ['name'],
        descending: false,
        where: [
          {
            field: 'projectId',
            operator: '==',
            value: this.projectId,
          },
        ],
      });

      this.escalationTypes = response.data;
      this.filteredEscalationTypes = this.escalationTypes;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  showNextPage() {
    this.pageCount += 1;
  }

  handleFilters() {
    this.filteredEscalationTypes = this.escalationTypes;

    const term = this.searchText?.trim().toLowerCase();

    if (term) {
      this.filteredEscalationTypes = this.escalationTypes.filter((t) => t.name.toLowerCase().includes(term));
    }
  }

  async createEscalationType() {
    const modal = await this.modalCtrl.create({
      component: EditEscalationTypeComponent,
      cssClass: 'modal-tall',
      componentProps: {
        projectId: this.projectId,
      },
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getEscalationTypes();
    });
  }

  async editEscalationType(id: string) {
    this.navCtrl.navigateForward(this.router.url, {
      queryParams: {
        id,
        projectId: this.projectId,
      },
    });
  }
}
